
import Vue from 'vue';
import IconGeolocation from '@components/icons/icon-geolocation.svg';
import { Component, Prop, Watch } from 'vue-property-decorator';
import {Address, Location, ZoneMutation} from '@clients/myTrips';
import ZoneMapbox from '@components/settings/zone/ZoneMapbox.vue';
import AddressInputField from '@components/new-styleguide/form/AddressInputField.vue';
import LocationAddress from '@/models/LocationAddress';
import { namespace } from 'vuex-class';
import { featureToAddress, featureToLocation } from '@util/mapbox';
import AppContent from '@components/new-styleguide/AppContent.vue';

const GeographicsModule = namespace('Geographics');

@Component({
  name: 'ZoneSelect',
  components: {
    AppContent,
    AddressInputField,
    ZoneMapbox,
    IconGeolocation
  }
})

export default class ZoneSelect extends Vue {
  @Prop()
  private value: ZoneMutation;

  @Prop({ default: false })
  private loading: boolean;

  @Prop()
  private initialAddress: Address;

  @GeographicsModule.Action('fetchPlaceNames')
  private fetchPlaceNames;

  @GeographicsModule.Getter('getPlaceNamesResponse')
  private getPlaceNamesResponse;

  private address: LocationAddress = null;

  private radiuses = [20, 50, 100, 200, 300, 400];

  get radiusValue() {
    return this.loading ? '-' : `${this.value.radius}m`;
  }

  @Watch('value')
  onValueInitialChange(newValue: ZoneMutation, oldValue: ZoneMutation) {
    if ((!oldValue.latitude || !oldValue.longitude) && newValue.latitude && newValue.longitude) {
      if (this.initialAddress) {
        this.address = {
          location: {
            longitude: newValue.longitude,
            latitude: newValue.latitude
          },
          address: { ...this.initialAddress }
        };
      } else {
        this.changeAddress({ longitude: newValue.longitude, latitude: newValue.latitude });
      }
    }
  }

  mounted() {
    if (this.value.latitude && this.value.longitude) {
      if (this.initialAddress) {
        this.address = {
          location: {
            longitude: this.value.longitude,
            latitude: this.value.latitude
          },
          address: { ...this.initialAddress }
        };
      } else {
        this.changeAddress({ longitude: this.value.longitude, latitude: this.value.latitude });
      }
    }
  }

  private handleNotificationChange(e: { target: HTMLInputElement }) {
    this.input(e.target.checked, 'shortParkingZoneNotification');
  }

  private handleSliderInput(e: { target: HTMLInputElement }) {
    this.value.radius = this.radiuses[e.target.value];
    this.input(this.value.radius, 'radius');
  }

  private handleLocationButton(location: Location) {
    this.handleLocationInput(location);
  }

  private handleAddressChange(address: LocationAddress) {
    const input = { ...this.value };

    input.longitude = address.location.longitude;
    input.latitude = address.location.latitude;
    this.address = address;

    this.$emit('input', input);
  }

  private async handleLocationInput(location: Location) {
    const input = { ...this.value };

    input.longitude = location.longitude;
    input.latitude = location.latitude;

    await this.changeAddress(location);

    this.$emit('input', input);
  }

  private async changeAddress(location: Location) {
    const results = await this.fetchPlaceNames({ longitude: location.longitude, latitude: location.latitude,  poi: true })

    if (results && results.features && results.features[0]) {
      this.address = {
        address: featureToAddress(results.features[0]),
        location: featureToLocation(results.features[0])
      };
    } else {
      this.address = null;
    }
  }

  private input(value, name) {
    const input = { ...this.value };

    input[name] = value;

    this.$emit('input', input);
  }

  get location(): Location {
    return this.value.latitude && this.value.longitude && { latitude: this.value.latitude, longitude: this.value.longitude };
  }
}
